import type {
  CentPrecisionMoney,
  TypedMoney,
} from "@commercetools/platform-sdk";
import is from "@sindresorhus/is";
import { compact } from "lodash";
import type { ReadonlyDeep } from "type-fest";

const isTypedMoney = (value: unknown): value is TypedMoney => {
  return (
    is.plainObject(value) &&
    is.number(value.centAmount) &&
    is.string(value.currencyCode) &&
    is.number(value.fractionDigits)
  );
};

export const moneyToNumber = (money?: TypedMoney): number => {
  if (!money) return 0;

  return (
    ("preciseAmount" in money ? money.preciseAmount : money.centAmount) /
    10 ** money.fractionDigits
  );
};

export const numberToMoney = (
  value: number,
  currencyCode: string,
  fractionDigits = 2,
): CentPrecisionMoney => {
  const centAmount = Math.round(value * 10 ** fractionDigits);

  return {
    type: "centPrecision",
    currencyCode,
    fractionDigits,
    centAmount,
  };
};

/**
 * Returns true if it is the same money
 */
export const compareMoney = (
  moneyA?: TypedMoney,
  moneyB?: TypedMoney,
): boolean => {
  if (!moneyA || !moneyB) return false;

  return (
    moneyA.currencyCode === moneyB.currencyCode &&
    moneyToNumber(moneyA) === moneyToNumber(moneyB)
  );
};

/**
 *
 * @param money
 * @returns
 */
export const combineMoney = (
  money: [TypedMoney, ...TypedMoney[]],
): CentPrecisionMoney => {
  const currencyCode = money[0].currencyCode;
  let maxFractionDigits = money[0].fractionDigits;
  let amount = 0;

  for (const currentMoney of money) {
    if (currentMoney.currencyCode !== currencyCode) {
      throw new Error("Cannot combine money with different currencies");
    }

    if (currentMoney.fractionDigits > maxFractionDigits) {
      maxFractionDigits = currentMoney.fractionDigits;
    }

    amount += moneyToNumber(currentMoney);
  }

  return numberToMoney(amount, currencyCode, maxFractionDigits);
};

export const invertMoney = (money: TypedMoney): CentPrecisionMoney =>
  numberToMoney(
    -moneyToNumber(money),
    money.currencyCode,
    money.fractionDigits,
  );

export const getVariantPrice = (
  variant: ReadonlyDeep<{
    id: string | number;
    originalId?: number;
    sku?: string | null;
    key?: string | null;
    prices?: Array<{
      country?: string | null;
      value: { currencyCode: string };
    }>;
  }>,
  currency: string,
  country: string | undefined,
): TypedMoney => {
  const availablePrices = compact(
    variant.prices
      ? variant.prices.filter((price) => {
          return (
            price &&
            price.value?.currencyCode === currency &&
            (!price.country || price.country === country)
          );
        })
      : [],
  );

  if (availablePrices.length === 0) {
    throw new Error(
      `No price found for currency ${currency} and country ${country} for ${variant.sku || variant.key || variant.originalId || variant.id}`,
    );
  }

  // Prices with country are prioritized
  const sortedPrices = availablePrices.sort((a, b) => {
    if (a.country && !b.country) return -1;
    if (!a.country && b.country) return 1;

    return 0;
  });

  const priceValue = sortedPrices[0]!.value!;

  if (!isTypedMoney(priceValue)) {
    throw new Error(
      `Invalid price format: ${JSON.stringify(priceValue)} for ${variant.sku || variant.key || variant.originalId || variant.id}`,
    );
  }

  return priceValue;
};
